/* eslint-disable react/prop-types */
/* eslint-disable import/no-cycle */

import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import Box from '@ntuctech/devex-tangram/Box';
import Typography from '@ntuctech/devex-tangram/Typography';
import { object as YupObject } from 'yup';
import { Formik } from 'formik';
import _get from 'lodash.get';

import { useAuth0 } from '../../Auth0Provider';
import { useFlowControl } from '../../FlowControlProvider';
import { FLOW_STEPS, SCHEMA, FLAGS } from '../../../constants';

import Api from '../../../services/api';
import Env from '../../../services/environment';

import ErrorBanner from '../../theme/ErrorBanner';
import PageContent from '../../theme/PageContent';
import PageHeader from '../../theme/PageHeader';
import SimpleModal from '../../theme/SimpleModal';
import ModalPwdResetEmailSent from '../../theme/SimpleModal/ModalPwdResetEmailSent';
import FormFooter from '../../theme/FormFooter';
import TnC from '../../theme/TnC';
import { svgApple, svgGoogle } from '../../theme/Icons';

import InputValidator, { verifiedMobileRegex } from '../../../services/input-validator';
import Logger from '../../../services/logger';

import WelcomeForm from './WelcomeForm';

import { useFeatureFlag } from '../../FeatureFlagProvider';

const INPUT_FIELD_NAME = 'emailOrPhone';
const GA_SCREEN_NAME = 'SSO - Welcome';
const NDI_EMBED_JS_ID = 'NdiEmbedAuthJs';

const SpQrContainer = styled.div`
  width: 18.625rem;
  margin: auto;
`;

const LoginSchema = YupObject().shape({
  [INPUT_FIELD_NAME]: InputValidator.emailOrPhoneValidation(SCHEMA.emailOrPhone, INPUT_FIELD_NAME),
});

const DividerOrWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  user-select: none;
  height: 2.25rem;
  margin: 1rem 0;

  .divider__line {
    height: 0.0625rem;
    background-color: #cccccc;
    width: auto;
    flex: 1;
  }

  .divider__text--or {
    color: #cccccc;
    margin: 0 0.75rem;
  }
`;

const DividerOr = () => (
  <DividerOrWrapper>
    <div className="divider__line" />
    <Typography className="divider__text--or" variant="body3">
      or
    </Typography>
    <div className="divider__line" />
  </DividerOrWrapper>
);

const SocialSignInBtn = styled.div`
  margin: 1rem 0;
  border-radius: 0.25rem;
  box-sizing: border-box;
  padding: 0 1rem;
  border: 0.0625rem solid #333333;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2.25rem;
  cursor: pointer;
  user-select: none;

  svg {
    height: 1rem;
    width: 1rem;
    margin-right: 0.5rem;
  }
`;

const SingpassLogoFull = styled.span`
  background-image: url('https://media.nedigital.sg/linkpass/files/001fb92f-38fa-44ac-ad34-b542dde05304/Singpass_Logo_Full.svg');
  width: 4.75rem;
  height: 1rem;
  display: inline-block;
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100%;
  margin-left: 0.375rem;
  margin-top: 0.125rem;
`;

const Welcome = () => {
  const {
    isLoading,
    forgotPassword,
    userContext,
    updateUserContext,
    resetUserContext,
    socialSignIn,
    trackPwl,
    shouldShowRedirectError,
    setRedirectErrorShownCookie,
    initSingpass,
  } = useAuth0();

  const { goNext, reStart } = useFlowControl();
  const [error, setError] = useState(null);

  const { isTreatmentOn } = useFeatureFlag();
  const isSingpassEnabled = isTreatmentOn(FLAGS.SINGPASS_LOGIN);

  const [isPwdNotSet, setIsPwdNotSet] = React.useState(false);
  const [isPwdResetEmailSent, setIsPwdResetEmailSent] = React.useState(false);
  const [needsReattempt, setNeedsReattempt] = React.useState(false);

  const formikBagRef = useRef({});

  const resetFormValues = () => {
    const { resetForm } = formikBagRef.current;
    if (resetForm) {
      resetForm();
    }
  };

  const clearAndRestart = React.useCallback(() => {
    trackPwl({
      screen_name: GA_SCREEN_NAME,
      event_action: 'Popup Click',
      event_label: 'SignIn_Email_NoPass_Login',
    });

    setIsPwdNotSet(false);
    reStart();
    resetUserContext();
    resetFormValues();
  }, [reStart, resetUserContext, trackPwl]);

  const sendPasswordResetEmail = React.useCallback(() => {
    trackPwl({
      screen_name: GA_SCREEN_NAME,
      event_action: 'Popup Click',
      event_label: 'SignIn_Email_NoPass_Reset',
    });

    const emailObj = { email: userContext.email };

    setError(null);
    forgotPassword(emailObj, (err) => {
      setIsPwdNotSet(false);

      if (err) {
        setError(err);
        Logger.error(err);
        return;
      }

      setIsPwdResetEmailSent(true);
    });
  }, [userContext, forgotPassword, trackPwl]);

  const restartWithEmail = React.useCallback(() => {
    setIsPwdResetEmailSent(false);
    reStart();
    resetUserContext();
    resetFormValues();
  }, [reStart, resetUserContext]);

  const initialValues = React.useMemo(
    () => ({ [INPUT_FIELD_NAME]: userContext.prefillEmail || '' }),
    [userContext],
  );

  const handleEmailFlow = React.useCallback(
    async (values, { resetForm, setSubmitting }) => {
      const email = values[INPUT_FIELD_NAME].trim();

      updateUserContext({ email });

      setError(null);

      try {
        const res = await Api.getEmailAvailability({ email });

        if (res.available) {
          trackPwl({
            screen_name: GA_SCREEN_NAME,
            event_action: 'Popup Show',
            event_label: 'Main_Signup',
          });

          goNext(FLOW_STEPS.EMAIL_SIGNUP);
        } else if (res.login_allowed) {
          trackPwl({
            screen_name: GA_SCREEN_NAME,
            event_action: 'Popup Show',
            event_label: 'Main_Email_SignIn',
          });

          goNext(FLOW_STEPS.EMAIL_LOGIN);
        } else {
          trackPwl({
            screen_name: GA_SCREEN_NAME,
            event_action: 'Popup Show',
            event_label: 'SignIn_Email_NoPass',
          });

          setIsPwdNotSet(true);
        }

        formikBagRef.current.resetForm = resetForm;
      } catch (e) {
        setError(e);
        Logger.error(e);
      }

      setSubmitting(false);
    },
    [goNext, updateUserContext, trackPwl],
  );

  const handleMobileFlow = React.useCallback(
    async (values, actions) => {
      const phoneNumber = values[INPUT_FIELD_NAME].trim();

      goNext(FLOW_STEPS.VERIFY_PHONE_NUMBER);
      updateUserContext({ phoneNumber });
      actions.setSubmitting(false);
    },
    [goNext, updateUserContext],
  );

  const onSubmit = React.useCallback(
    (values, actions) => {
      const fieldValue = values[INPUT_FIELD_NAME].trim();
      const mobileFlow = verifiedMobileRegex.test(fieldValue);

      const submitEvent = {
        screen_name: GA_SCREEN_NAME,
        event_action: needsReattempt ? 'Popup Click' : 'Click',
        event_label: needsReattempt
          ? `Main_${mobileFlow ? 'Mobile' : 'Email'}_Error_Next`
          : 'Main_Next',
      };

      trackPwl(submitEvent);

      if (mobileFlow) {
        handleMobileFlow(values, actions);
      } else {
        handleEmailFlow(values, actions);
      }
    },
    [handleEmailFlow, handleMobileFlow, needsReattempt, trackPwl],
  );

  const handleSocialSignInClickApple = React.useCallback(() => {
    trackPwl({
      screen_name: GA_SCREEN_NAME,
      event_action: 'Click',
      event_label: 'Main_Apple',
    });
    socialSignIn('apple');
  }, [socialSignIn, trackPwl]);

  const handleSocialSignInClickGoogle = React.useCallback(() => {
    trackPwl({
      screen_name: GA_SCREEN_NAME,
      event_action: 'Click',
      event_label: 'Main_Google',
    });
    socialSignIn('google-oauth2');
  }, [socialSignIn, trackPwl]);

  const handleSocialSignInClickSingPass = React.useCallback(() => {
    trackPwl({
      screen_name: GA_SCREEN_NAME,
      event_action: 'Click',
      event_label: 'Main_Singpass',
    });
    socialSignIn('singpass');
  }, [socialSignIn, trackPwl]);

  const emailMobileForm = React.useMemo(
    () => (
      <>
        {error && <ErrorBanner error={error} />}
        <Formik
          initialValues={initialValues}
          enableReinitialize
          onSubmit={onSubmit}
          validationSchema={LoginSchema}
        >
          {(formikProps) => (
            <WelcomeForm
              {...formikProps}
              needsReattempt={needsReattempt}
              setNeedsReattempt={setNeedsReattempt}
            />
          )}
        </Formik>
      </>
    ),
    [error, initialValues, needsReattempt, onSubmit],
  );

  const socialSignInButtons = React.useMemo(
    () => (
      <>
        <DividerOr />
        <SocialSignInBtn
          data-testid="SocialSignInApple"
          onClick={handleSocialSignInClickApple}
          title="Continue with Apple"
        >
          {svgApple} <Typography variant="subhead1">Continue with Apple</Typography>
        </SocialSignInBtn>
        <SocialSignInBtn
          data-testid="SocialSignInGoogle"
          onClick={handleSocialSignInClickGoogle}
          title="Continue with Google"
        >
          {svgGoogle} <Typography variant="subhead1">Continue with Google</Typography>
        </SocialSignInBtn>
        {isSingpassEnabled && !userContext.isSingpassReAuth && (
          <SocialSignInBtn
            data-testid="SocialSignInSingPass"
            onClick={handleSocialSignInClickSingPass}
            title="Continue with Singpass"
          >
            <Typography variant="subhead1">Continue with</Typography>
            <SingpassLogoFull />
          </SocialSignInBtn>
        )}
      </>
    ),
    [
      handleSocialSignInClickApple,
      handleSocialSignInClickGoogle,
      handleSocialSignInClickSingPass,
      isSingpassEnabled,
    ],
  );

  const welcomeName = React.useMemo(
    () => _get(userContext, 'clientBasedUi.welcomeName'),
    [userContext],
  );

  const pageTitle = React.useMemo(() => {
    const { isSingpassReAuth } = userContext;
    if (isSingpassReAuth) {
      return 'One last step to connect your Singpass account';
    } else {
      return welcomeName === 'singpass_proxy'
        ? 'Continue with Singpass'
        : `Welcome to ${welcomeName}!`;
    }
  }, [userContext, welcomeName]);

  React.useEffect(
    () => {
      trackPwl({
        screen_name: GA_SCREEN_NAME,
        event_action: 'Landed',
        event_label: 'Main',
      });
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  React.useEffect(
    () => {
      let isMounted = true;

      const beforeUnload = () => {
        setRedirectErrorShownCookie();
      };

      if (isMounted && shouldShowRedirectError) {
        window.addEventListener('beforeunload', beforeUnload);
      }

      return () => {
        isMounted = false;
        window.removeEventListener('beforeunload', beforeUnload);
      };
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [shouldShowRedirectError],
  );

  React.useEffect(() => {
    if (shouldShowRedirectError) {
      setError(userContext.redirectError);
    }
  }, [shouldShowRedirectError, userContext]);

  React.useEffect(() => {
    // init Singpass
    if (userContext.isSingpassLogin) {
      const existingScript = document.getElementById(NDI_EMBED_JS_ID);

      if (!existingScript) {
        const EnvVars = Env.vars();
        const ndiEndpoint = EnvVars.NDI_ENDPOINT;

        const script = document.createElement('script');
        script.src = `${ndiEndpoint}/static/ndi_embedded_auth.js`;
        script.id = NDI_EMBED_JS_ID;
        document.body.appendChild(script);
        script.onload = () => {
          initSingpass();
        };
      } else {
        initSingpass();
      }
    }
  }, [userContext, initSingpass]);

  return (
    <>
      <PageContent
        data-testid="WelcomePage_Sp"
        {...(!userContext.isSingpassLogin && { style: { display: 'none' } })}
      >
        <SpQrContainer id="linkpass-sso-sp" />
      </PageContent>
      <PageContent
        data-testid="WelcomePage_Link"
        {...(userContext.isSingpassLogin && { style: { display: 'none' } })}
      >
        <SimpleModal
          open={isPwdNotSet}
          title="You have not set your password"
          body="Please log in via mobile number or reset your password"
          ctaText="Log in via mobile"
          ctaAction={clearAndRestart}
          secondaryText="Reset password"
          secondaryAction={sendPasswordResetEmail}
          secondaryLoading={isLoading}
          testId="modalPwdNotSet"
        />
        <ModalPwdResetEmailSent open={isPwdResetEmailSent} ctaAction={restartWithEmail} />
        <PageHeader header={pageTitle} spacing={{ mb: 3 }} />
        <Box mb={1} data-testid="WelcomeDesc">
          <Typography variant="subhead1">
            Log in or create an account with one of these methods.
          </Typography>
        </Box>
        {emailMobileForm}

        {socialSignInButtons}

        <Box mt={2}>
          <FormFooter>
            <TnC type="welcome" />
          </FormFooter>
        </Box>
      </PageContent>
    </>
  );
};

export default Welcome;
